import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Check, ArrowRight, Scale, FileText, Handshake, Shield, Users, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LIBRARY_IMAGE } from "@/lib/content";

const CAPACITIES = [
  {
    icon: Scale,
    title: "Business & Tax Co-Counsel",
    body: "Business formation, M&A, buy-sell agreements, entity restructuring, and the tax planning that surrounds each. Full attorney + CPA analysis from one desk.",
  },
  {
    icon: FileText,
    title: "Trust, Probate & Estate Matters",
    body: "Complex trust drafting, probate administration, guardianship, elder law, and the coordinated tax planning that most litigation and family-law practices are not resourced to handle.",
  },
  {
    icon: Shield,
    title: "Chapter 7, 11 & Subchapter V",
    body: "Business and individual bankruptcy — restructuring, liquidation, and the Sub V small-business reorganization. Admitted to N.D. and E.D. Texas Bankruptcy Courts since 2001.",
  },
  {
    icon: Users,
    title: "Cross-Border & Immigration-Adjacent Tax",
    body: "Pre-immigration tax planning, EB-5 investor structuring, and U.S.–India / South Asian family matters coordinated with in-country counsel of record.",
  },
];

const ARRANGEMENTS = [
  {
    no: "01",
    title: "Straight Referral",
    body: "You send the client. We handle the matter as principal counsel. Client relationship transfers to LSC. Standard arrangement for matters entirely outside your practice area.",
  },
  {
    no: "02",
    title: "Co-Counsel Engagement",
    body: "We serve as co-counsel on discrete tax or business-law aspects of a matter you continue to lead. Joint engagement letter. Fee division per Rule 1.04(f) with client consent in writing.",
  },
  {
    no: "03",
    title: "Of Counsel / White-Label Support",
    body: "For firms without in-house tax or business capacity, we serve quietly as your firm's tax and business attorney — deliverables in your firm's letterhead where the client relationship warrants.",
  },
  {
    no: "04",
    title: "Ongoing Referral Relationship",
    body: "Some Texas firms send us their tax-adjacent, business-succession, and Chapter 11 matters as a standing arrangement. Retained counsel status; priority scheduling; annual case review.",
  },
];

const ETHICS = [
  "All fee-division arrangements strictly comply with Texas Disciplinary Rule 1.04(f) — division in proportion to services performed, or by written agreement of joint responsibility, with written client consent, and only where the total fee is reasonable.",
  "Written engagement letters and, where required, joint-responsibility acknowledgments furnished before any work commences.",
  "No client capture — where you have a continuing relationship with the client outside the referred matter, we return the client to you at matter close, without follow-up solicitation.",
  "Conflict checks run within two business days of any referral. Prior clearances retained for reference.",
  "Malpractice coverage current and disclosed on request. Copies of the firm's declarations page furnished to referring counsel.",
];

export default function ForCounsel() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    firm_name: "",
    bar_number: "",
    location: "",
    message: "",
    preferred_contact: "email",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit =
    form.full_name.trim() &&
    form.email.trim() &&
    form.firm_name.trim() &&
    form.message.trim().length >= 15;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/intake", {
        ...form,
        practice_area: "Peer Counsel Referral",
        stage: "formation",
        matter_class: "peer_counsel",
        referral_source: `${form.firm_name}${form.bar_number ? ` (Bar #${form.bar_number})` : ""}`,
      });
      setDone(true);
      toast.success("Peer referral received.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid="for-counsel-page">
      {/* Private page — deliberately unlinked from primary navigation and
          excluded from indexing. Shared by direct URL only. */}
      <Helmet>
        <title>For Counsel — Peer Referral & Co-Counsel · Lone Star Counsel</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="A private page for Texas attorneys. Peer referral, co-counsel, and Of Counsel engagement information for Lone Star Counsel — business, tax, trust, and bankruptcy matters." />
      </Helmet>

      {/* HERO */}
      <section className="relative panel-navy min-h-[65vh] flex items-end" data-testid="fc-hero">
        <div className="absolute inset-0">
          <img src={LIBRARY_IMAGE} alt="" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/75 via-navy/60 to-navy/95" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-36 w-full">
          <div className="kicker kicker-ivory">For Counsel</div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.42em] text-brass font-medium">
            A Private Page for Texas Attorneys
          </div>
          <h1 className="mt-8 font-display text-ivory text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl">
            Peer referral. <br/>
            <span className="italic font-cormorant text-brass">Co-counsel. Standing support.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-cormorant italic text-xl md:text-2xl text-ivory/85 leading-snug">
            For litigation, family-law, PI, and general practice firms whose clients arrive with
            business, tax, trust, or restructuring matters outside your primary practice.
          </p>
        </div>
      </section>

      {/* INTRO — professional-to-professional tone */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="fc-intro">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="kicker">A Note to Counsel</div>
            <h2 className="mt-6 font-display text-3xl md:text-4xl leading-[1.05]">
              A quiet<br/>
              <span className="italic font-cormorant text-oxblood">professional courtesy.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 text-base md:text-lg font-light text-slate2 leading-relaxed space-y-5">
            <p className="dropcap">
              Fellow member of the Bar — thank you for finding this page. It is unlinked from our
              main navigation because it is not for the public. It is a candid brief for other
              Texas attorneys on how this firm accepts, handles, and (where appropriate) shares
              matters with referring counsel.
            </p>
            <p>
              Darryl V. Pratt has practiced in Texas since 1997 as both an attorney and a CPA. The
              firm handles business, tax, trust, probate, elder-law, and Chapter 7 / 11 / Subchapter
              V matters as principal counsel — and stands available as co-counsel or Of Counsel
              support for firms whose clients present matters outside their primary practice area.
            </p>
            <p>
              Fee-division arrangements are structured in strict compliance with Texas Disciplinary
              Rule 1.04(f). Engagement letters and joint-responsibility acknowledgments are
              furnished before any work begins.
            </p>
          </div>
        </div>
      </section>

      {/* WHERE LSC ACCEPTS REFERRALS */}
      <section className="bg-linen py-20 md:py-28" data-testid="fc-capacities">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="mb-12">
            <div className="kicker">Referral Capacity</div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
              Matter types<br/>
              <span className="italic font-cormorant text-oxblood">accepted as principal or co-counsel.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            {CAPACITIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <article key={i} className="hairline-top pt-6" data-testid={`fc-capacity-${i}`}>
                  <div className="text-brass"><Icon size={26} strokeWidth={1.2} /></div>
                  <h3 className="mt-5 font-display text-2xl text-navy leading-[1.15]">{c.title}</h3>
                  <p className="mt-3 text-base text-slate2 font-light leading-relaxed">{c.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARRANGEMENT TYPES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="fc-arrangements">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="kicker">Arrangement Options</div>
            <h2 className="mt-6 font-display text-3xl md:text-4xl leading-[1.05]">
              Four ways<br/>
              <span className="italic font-cormorant text-oxblood">we work with referring counsel.</span>
            </h2>
          </div>
          <ol className="lg:col-span-7 lg:col-start-6 space-y-8">
            {ARRANGEMENTS.map((a) => (
              <li key={a.no} className="grid grid-cols-12 gap-4 items-baseline hairline-top pt-6" data-testid={`fc-arr-${a.no}`}>
                <div className="col-span-2 font-cormorant italic text-3xl md:text-4xl text-brass">{a.no}</div>
                <div className="col-span-10">
                  <h3 className="font-display text-xl md:text-2xl text-navy">{a.title}</h3>
                  <p className="mt-2 text-base text-slate2 font-light leading-relaxed">{a.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ETHICS & COMPLIANCE */}
      <section className="panel-navy py-20 md:py-28" data-testid="fc-ethics">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">Ethics &amp; Compliance</div>
              <h2 className="mt-6 font-display text-3xl md:text-4xl text-ivory leading-[1.05]">
                Rule 1.04(f)<br/>
                <span className="italic font-cormorant text-brass">by the letter.</span>
              </h2>
              <p className="mt-6 font-cormorant italic text-lg text-ivory/85 leading-snug max-w-md">
                Nothing informal. Nothing verbal-only. Every referral relationship documented in a
                way both firms can defend to the State Bar without hesitation.
              </p>
            </div>
            <ul className="lg:col-span-6 lg:col-start-7 space-y-5 text-ivory/85 font-light">
              {ETHICS.map((t, i) => (
                <li key={i} className="flex items-start gap-4 hairline-top pt-5" data-testid={`fc-ethic-${i}`}>
                  <Check size={18} className="text-brass shrink-0 mt-1" />
                  <span className="text-base md:text-lg leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="peer-refer" className="bg-ivory py-20 md:py-28" data-testid="fc-form-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker">Peer Introduction</div>
              <h2 className="mt-6 font-display text-3xl md:text-4xl leading-[1.05]">
                Send a<br/>
                <span className="italic font-cormorant text-oxblood">peer referral.</span>
              </h2>
              <p className="mt-6 text-slate2 font-light leading-relaxed">
                For discussion of a specific matter, an ongoing referral arrangement, or Of Counsel
                support. Darryl will respond personally within two business days.
              </p>
            </div>

            {done ? (
              <div className="lg:col-span-7 lg:col-start-6" data-testid="fc-success">
                <div className="kicker">Received</div>
                <h3 className="mt-4 font-display text-3xl md:text-4xl leading-[1.02]">
                  Received. <span className="italic font-cormorant text-oxblood">Thank you.</span>
                </h3>
                <p className="mt-6 text-lg text-slate2 font-light leading-relaxed">
                  A conflict check will be run within two business days, followed by a direct
                  response to discuss the matter, arrangement, or standing relationship.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to="/" className="btn-ghost" data-testid="fc-return-home">Return to Public Site</Link>
                </div>
              </div>
            ) : (
              <form className="lg:col-span-7 lg:col-start-6 space-y-6" onSubmit={submit} data-testid="fc-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your Name" required>
                    <input className="field" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="fc-full-name" />
                  </Field>
                  <Field label="Email" required>
                    <input className="field" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="fc-email" />
                  </Field>
                  <Field label="Firm Name" required>
                    <input className="field" value={form.firm_name} onChange={(e) => update("firm_name", e.target.value)} data-testid="fc-firm-name" />
                  </Field>
                  <Field label="Texas Bar Number">
                    <input className="field" value={form.bar_number} onChange={(e) => update("bar_number", e.target.value)} data-testid="fc-bar-number" />
                  </Field>
                  <Field label="Telephone">
                    <input className="field" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="fc-phone" />
                  </Field>
                  <Field label="Firm City">
                    <input className="field" value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="fc-location" placeholder="Dallas, Houston, etc." />
                  </Field>
                </div>

                <Field label="Matter or Discussion" required>
                  <textarea
                    className="field"
                    rows={7}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="fc-message"
                    placeholder="Matter type (no confidential client identifying information), arrangement type sought (straight referral, co-counsel, Of Counsel), and any relevant timing. If discussing a standing referral relationship, please note that."
                  />
                </Field>

                <Field label="Preferred Contact">
                  <select className="field" value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)} data-testid="fc-preferred-contact">
                    <option value="email">By Email</option>
                    <option value="phone">By Telephone</option>
                  </select>
                </Field>

                <div className="hairline pt-6 flex flex-wrap items-center justify-between gap-6">
                  <p className="text-xs text-slate2/70 font-cormorant italic max-w-md leading-snug">
                    This is a private page for Texas attorneys. Submission does not create an
                    attorney–attorney or attorney–client relationship. A conflict check will be
                    completed before any substantive discussion.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting || !canSubmit}
                    className="btn-brass disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="fc-submit"
                  >
                    {submitting ? "Submitting…" : (<>Send Introduction <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* DIRECT-LINE FOOTER */}
      <section className="bg-linen py-14" data-testid="fc-direct">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="kicker">Direct Line</div>
            <p className="mt-3 font-cormorant italic text-lg text-slate2 max-w-md leading-snug">
              For time-sensitive matters, referring attorneys are welcome to call the firm
              directly. Darryl personally answers between 9 a.m. and 5 p.m. Central.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Handshake size={20} className="text-brass" />
            <a href="/contact" className="font-display text-3xl text-navy hover:text-brass transition">
              (972) 712-1515
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="field-label mb-2">
        {label} {required && <span className="text-oxblood">·</span>}
      </div>
      {children}
    </label>
  );
}
